/**
 * Sunprojects Kombi-Angebot Review Page
 * - Wird angezeigt, wenn ein PDF im Format "sunprojects_combined" hochgeladen wurde
 * - Zeigt PV-Sub-Angebot + WP-Sub-Angebot nebeneinander
 * - Erlaubt Kundenverknüpfung oder Anlage eines neuen Kunden
 * - Finalize erstellt 2 separate Angebote
 */

import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Badge } from "../components/ui/badge";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import {
  ArrowLeft, CheckCircle, AlertTriangle, Loader2, Sun, Flame, Trash2, Plus, UserPlus,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const WP_GROUPS = [
  { key: "waermepumpe", label: "Wärmepumpe" },
  { key: "speicher", label: "Speicher" },
  { key: "hydraulik", label: "Hydraulik" },
  { key: "installation", label: "Installation" },
  { key: "zusatz", label: "Zusatz" },
  { key: "sonstiges", label: "Sonstiges" },
];

const PV_CATEGORIES = ["material", "montage", "elektro", "planung", "sonstiges"];

const fmtEur = (v) =>
  v === null || v === undefined
    ? "—"
    : new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(v);

export default function CombinedOfferImportReviewPage() {
  const { importFileId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [customers, setCustomers] = useState([]);

  // Editable state
  const [customerMode, setCustomerMode] = useState("link"); // "link" | "create"
  const [customerId, setCustomerId] = useState("");
  const [newCustomer, setNewCustomer] = useState({
    salutation: "Herr", first_name: "", last_name: "", company_name: "",
    address: "", email: "", phone: "",
  });
  const [pvOffer, setPvOffer] = useState(null);
  const [wpOffer, setWpOffer] = useState(null);
  const [createPv, setCreatePv] = useState(true);
  const [createWp, setCreateWp] = useState(true);

  useEffect(() => {
    loadData();
    loadCustomers();
  }, [importFileId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/offer-import/import-file/${importFileId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Import-Datei nicht gefunden");
      const file = await res.json();
      setImportFile(file);
      const pd = file.parsed_data_json || {};
      // Customer prefill
      const c = pd.customer || {};
      const fullName = (c.name || "").trim();
      const [first, ...rest] = fullName.split(/\s+/);
      setNewCustomer({
        salutation: c.salutation || "Herr",
        first_name: first || "",
        last_name: rest.join(" ") || "",
        company_name: "",
        address: [c.street, [c.zip, c.city].filter(Boolean).join(" ")].filter(Boolean).join("\n"),
        email: c.email || "",
        phone: c.phone || "",
      });
      // Sub offers
      setPvOffer({
        positions: (pd.pv_offer?.positions || []).map((p, i) => ({ ...p, id: `pv-${i}` })),
        subtotal_net: pd.pv_offer?.subtotal_net ?? 0,
        discount: pd.pv_offer?.discount ?? null,
        vat_rate: pd.pv_offer?.vat_rate ?? 0,
        vat_amount: pd.pv_offer?.vat_amount ?? 0,
        total_gross: pd.pv_offer?.total_gross ?? 0,
        title: `PV-Angebot ${pd.shared?.pv_system_kwp ? pd.shared.pv_system_kwp + ' kWp' : ''}`.trim(),
        offer_date: pd.shared?.offer_date || new Date().toISOString().slice(0, 10),
      });
      const wpData = pd.wp_offer || {};
      setWpOffer({
        positions: (wpData.positions || []).map((p, i) => ({
          ...p,
          id: `wp-${i}`,
          product_group: p.product_group || "sonstiges",
        })),
        subtotal_net: wpData.subtotal_net ?? 0,
        vat_rate: wpData.vat_rate ?? 19,
        vat_amount: wpData.vat_amount ?? 0,
        total_gross: wpData.total_gross ?? 0,
        title: `Wärmepumpen-Angebot ${pd.shared?.hp_model || ''}`.trim(),
        heat_demand_kwh: wpData.hp_specifics?.heat_demand_kwh || "",
        current_heating_type: wpData.hp_specifics?.current_heating_type || "",
        current_energy_cost_eur: wpData.hp_specifics?.current_energy_cost_eur || "",
        expected_savings_eur: "",
      });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/customers/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setCustomers(await res.json());
    } catch {/* ignore */}
  };

  // Suche bestehenden Kunden anhand der PLZ aus dem PDF (Auto-Match)
  useEffect(() => {
    if (!customers.length || !newCustomer.address) return;
    const zipMatch = newCustomer.address.match(/\b(\d{5})\b/);
    if (!zipMatch) return;
    const zip = zipMatch[1];
    const lastName = newCustomer.last_name.toLowerCase();
    const candidate = customers.find(
      (c) =>
        (c.address || "").includes(zip) &&
        (lastName && (c.last_name || "").toLowerCase().includes(lastName))
    );
    if (candidate && customerMode === "link" && !customerId) {
      setCustomerId(candidate.id);
      toast.info(`Kunde "${candidate.first_name} ${candidate.last_name}" automatisch verknüpft`);
    }
  }, [customers, newCustomer.address, newCustomer.last_name]);

  // Item handlers
  const updatePvItem = (id, field, value) =>
    setPvOffer((prev) => ({
      ...prev,
      positions: prev.positions.map((p) => (p.id === id ? { ...p, [field]: value } : p)),
    }));
  const removePvItem = (id) =>
    setPvOffer((prev) => ({ ...prev, positions: prev.positions.filter((p) => p.id !== id) }));
  const addPvItem = () =>
    setPvOffer((prev) => ({
      ...prev,
      positions: [
        ...prev.positions,
        { id: `pv-new-${Date.now()}`, title: "", quantity: 1, unit: "Stk", unit_price: 0, total_price: 0, category: "material" },
      ],
    }));

  const updateWpItem = (id, field, value) =>
    setWpOffer((prev) => ({
      ...prev,
      positions: prev.positions.map((p) => (p.id === id ? { ...p, [field]: value } : p)),
    }));
  const removeWpItem = (id) =>
    setWpOffer((prev) => ({ ...prev, positions: prev.positions.filter((p) => p.id !== id) }));
  const addWpItem = (group = "sonstiges") =>
    setWpOffer((prev) => ({
      ...prev,
      positions: [
        ...prev.positions,
        { id: `wp-new-${Date.now()}`, title: "", quantity: 1, unit: "Stk", unit_price: 0, total_price: 0, product_group: group },
      ],
    }));

  // Recalc WP totals from line items live
  const wpRecalc = useMemo(() => {
    if (!wpOffer) return null;
    const sub = wpOffer.positions.reduce(
      (s, p) => s + (parseFloat(p.quantity) || 0) * (parseFloat(p.unit_price) || 0),
      0
    );
    const rate = parseFloat(wpOffer.vat_rate) || 0;
    const vat = sub * rate / 100;
    return { subtotal: sub, vat, gross: sub + vat };
  }, [wpOffer]);

  const handleFinalize = async () => {
    // Validation
    if (customerMode === "link" && !customerId) {
      toast.error("Bitte Kunden auswählen oder neuen Kunden anlegen");
      return;
    }
    if (customerMode === "create" && (!newCustomer.first_name || !newCustomer.last_name)) {
      toast.error("Vor- und Nachname sind Pflichtfelder");
      return;
    }
    if (!createPv && !createWp) {
      toast.error("Mindestens ein Teil (PV oder WP) muss erstellt werden");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        customer_id: customerMode === "link" ? customerId : null,
        create_customer: customerMode === "create" ? newCustomer : null,
        create_pv: createPv,
        create_wp: createWp,
        pv_offer: createPv
          ? {
              title: pvOffer.title,
              offer_date: pvOffer.offer_date,
              vat_rate: 0,
              positions: pvOffer.positions.map((p) => ({
                line_no: p.pos || "",
                title: p.title,
                description: p.description,
                quantity: parseFloat(p.quantity) || 1,
                unit: p.unit || "Stk",
                unit_price_net: parseFloat(p.unit_price) || 0,
                total_price_net: parseFloat(p.total_price) || (parseFloat(p.quantity) || 1) * (parseFloat(p.unit_price) || 0),
                category: p.category || "sonstiges",
                item_type: p.category || "sonstiges",
                is_billable: true,
                is_optional: false,
              })),
              discount: pvOffer.discount,
            }
          : null,
        wp_offer: createWp
          ? {
              title: wpOffer.title,
              vat_rate: wpOffer.vat_rate === "" || wpOffer.vat_rate == null || Number.isNaN(parseFloat(wpOffer.vat_rate)) ? 19 : parseFloat(wpOffer.vat_rate),
              positions: wpOffer.positions.map((p) => ({
                title: p.title,
                description: p.description,
                quantity: parseFloat(p.quantity) || 1,
                unit: p.unit || "Stk",
                unit_price: parseFloat(p.unit_price) || 0,
                product_group: p.product_group || "sonstiges",
              })),
              heat_demand_kwh: wpOffer.heat_demand_kwh ? parseFloat(wpOffer.heat_demand_kwh) : null,
              current_heating_type: wpOffer.current_heating_type || null,
              current_energy_cost_eur: wpOffer.current_energy_cost_eur ? parseFloat(wpOffer.current_energy_cost_eur) : null,
              expected_savings_eur: wpOffer.expected_savings_eur ? parseFloat(wpOffer.expected_savings_eur) : null,
            }
          : null,
      };

      const res = await fetch(`${API_URL}/api/offer-import/finalize-combined/${importFileId}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Import fehlgeschlagen");
      }
      const result = await res.json();
      toast.success(result.message || "Kombi-Import erfolgreich");
      // Navigate based on what was created
      if (result.pv_offer_id) {
        navigate(`/angebote-import/${result.pv_offer_id}`);
      } else if (result.wp_offer_id) {
        navigate(`/wp-angebote/${result.wp_offer_id}`);
      } else {
        navigate("/angebote");
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" data-testid="combined-review-loading" />
      </div>
    );
  }

  const pd = importFile?.parsed_data_json || {};
  const shared = pd.shared || {};
  const warnings = pd.warnings || [];

  // Group WP positions by product_group for display
  const wpByGroup = WP_GROUPS.reduce((acc, g) => {
    acc[g.key] = (wpOffer?.positions || []).filter((p) => (p.product_group || "sonstiges") === g.key);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="combined-review-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/angebote-import")} data-testid="back-btn">
          <ArrowLeft className="h-4 w-4 mr-2" /> Zurück
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Kombi-Angebot Review</h1>
          <p className="text-sm text-muted-foreground">
            Quelle: {importFile?.original_filename} · wird in <strong>PV</strong> +{" "}
            <strong>Wärmepumpe</strong> aufgeteilt
          </p>
        </div>
      </div>

      {warnings.length > 0 && (
        <Alert className="border-orange-300 bg-orange-50">
          <AlertTriangle className="h-4 w-4 text-orange-600" />
          <AlertDescription>
            {warnings.map((w, i) => (
              <div key={i}>{w}</div>
            ))}
          </AlertDescription>
        </Alert>
      )}

      {/* SHARED INFO */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Erkannte Stammdaten</CardTitle>
        </CardHeader>
        <CardContent className="grid md:grid-cols-4 gap-3 text-sm">
          <div><span className="text-muted-foreground">Angebotsdatum:</span> <strong>{shared.offer_date || "—"}</strong></div>
          <div><span className="text-muted-foreground">PV-Anlage:</span> <strong>{shared.pv_system_kwp ?? "—"} kWp</strong></div>
          <div><span className="text-muted-foreground">Speicher:</span> <strong>{shared.storage_kwh ?? "—"} kWh</strong></div>
          <div><span className="text-muted-foreground">Wärmepumpe:</span> <strong>{shared.hp_model || "—"}</strong></div>
        </CardContent>
      </Card>

      {/* CUSTOMER */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><UserPlus className="h-4 w-4" /> Kunde</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Button
              variant={customerMode === "link" ? "default" : "outline"}
              size="sm"
              onClick={() => setCustomerMode("link")}
              data-testid="customer-mode-link"
            >
              Bestehenden verknüpfen
            </Button>
            <Button
              variant={customerMode === "create" ? "default" : "outline"}
              size="sm"
              onClick={() => setCustomerMode("create")}
              data-testid="customer-mode-create"
            >
              Neu anlegen (mit PDF-Daten vorausgefüllt)
            </Button>
          </div>
          {customerMode === "link" ? (
            <Select value={customerId} onValueChange={setCustomerId}>
              <SelectTrigger data-testid="customer-link-select">
                <SelectValue placeholder="Kunde auswählen" />
              </SelectTrigger>
              <SelectContent>
                {customers.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.customer_number ? `${c.customer_number} – ` : ""}
                    {c.first_name} {c.last_name} {c.company_name ? `(${c.company_name})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label>Anrede</Label>
                <Select
                  value={newCustomer.salutation}
                  onValueChange={(v) => setNewCustomer({ ...newCustomer, salutation: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Herr">Herr</SelectItem>
                    <SelectItem value="Frau">Frau</SelectItem>
                    <SelectItem value="Firma">Firma</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div></div>
              <div><Label>Vorname *</Label><Input value={newCustomer.first_name} onChange={(e) => setNewCustomer({ ...newCustomer, first_name: e.target.value })} /></div>
              <div><Label>Nachname *</Label><Input value={newCustomer.last_name} onChange={(e) => setNewCustomer({ ...newCustomer, last_name: e.target.value })} /></div>
              <div className="md:col-span-2"><Label>Adresse (mehrzeilig)</Label><Input value={newCustomer.address} onChange={(e) => setNewCustomer({ ...newCustomer, address: e.target.value })} /></div>
              <div><Label>E-Mail</Label><Input type="email" value={newCustomer.email} onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })} /></div>
              <div><Label>Telefon</Label><Input value={newCustomer.phone} onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })} /></div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* SELECTOR */}
      <Card>
        <CardContent className="pt-6 flex flex-wrap gap-6 items-center">
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox checked={createPv} onCheckedChange={setCreatePv} data-testid="create-pv-checkbox" />
            <Sun className="h-4 w-4 text-yellow-500" /> PV-Angebot erstellen ({fmtEur(pvOffer?.total_gross)})
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox checked={createWp} onCheckedChange={setCreateWp} data-testid="create-wp-checkbox" />
            <Flame className="h-4 w-4 text-orange-500" /> WP-Angebot erstellen ({fmtEur(wpRecalc?.gross || wpOffer?.total_gross)})
          </label>
        </CardContent>
      </Card>

      <Tabs defaultValue="pv">
        <TabsList>
          <TabsTrigger value="pv" data-testid="tab-pv">
            <Sun className="h-4 w-4 mr-2" /> PV-Angebot ({pvOffer?.positions?.length || 0} Pos.)
          </TabsTrigger>
          <TabsTrigger value="wp" data-testid="tab-wp">
            <Flame className="h-4 w-4 mr-2" /> WP-Angebot ({wpOffer?.positions?.length || 0} Pos.)
          </TabsTrigger>
        </TabsList>

        {/* PV TAB */}
        <TabsContent value="pv" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center justify-between">
                <span>PV-Angebot Positionen (0% MwSt.)</span>
                <Button size="sm" variant="outline" onClick={addPvItem} data-testid="add-pv-item">
                  <Plus className="h-4 w-4 mr-1" /> Position
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Kategorie</TableHead>
                    <TableHead>Bezeichnung</TableHead>
                    <TableHead className="text-right">Menge</TableHead>
                    <TableHead>Einheit</TableHead>
                    <TableHead className="text-right">EP (€)</TableHead>
                    <TableHead className="text-right">Gesamt (€)</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(pvOffer?.positions || []).map((p) => (
                    <TableRow key={p.id}>
                      <TableCell>
                        <Select value={p.category || "sonstiges"} onValueChange={(v) => updatePvItem(p.id, "category", v)}>
                          <SelectTrigger className="h-8 w-32"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {PV_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Input value={p.title || ""} onChange={(e) => updatePvItem(p.id, "title", e.target.value)} className="h-8" />
                      </TableCell>
                      <TableCell className="text-right">
                        <Input type="number" step="0.01" value={p.quantity} onChange={(e) => updatePvItem(p.id, "quantity", e.target.value)} className="h-8 text-right w-20" />
                      </TableCell>
                      <TableCell>
                        <Input value={p.unit || "Stk"} onChange={(e) => updatePvItem(p.id, "unit", e.target.value)} className="h-8 w-20" />
                      </TableCell>
                      <TableCell className="text-right">
                        <Input type="number" step="0.01" value={p.unit_price ?? 0} onChange={(e) => updatePvItem(p.id, "unit_price", e.target.value)} className="h-8 text-right w-24" />
                      </TableCell>
                      <TableCell className="text-right">
                        <Input type="number" step="0.01" value={p.total_price ?? 0} onChange={(e) => updatePvItem(p.id, "total_price", e.target.value)} className="h-8 text-right w-24" />
                      </TableCell>
                      <TableCell>
                        <Button size="icon" variant="ghost" onClick={() => removePvItem(p.id)} data-testid={`remove-pv-${p.id}`}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
          <Card className="border-2 border-yellow-300 bg-yellow-50">
            <CardContent className="pt-6 text-right space-y-1">
              <div className="text-sm">Netto-Summe: <strong>{fmtEur(pvOffer?.subtotal_net)}</strong></div>
              {pvOffer?.discount && (
                <div className="text-sm text-red-600">
                  Rabatt: <strong>
                    {pvOffer.discount.type === "percentage"
                      ? `-${pvOffer.discount.value}%`
                      : fmtEur(-pvOffer.discount.value)}
                  </strong> ({pvOffer.discount.description || ""})
                </div>
              )}
              <div className="text-2xl font-bold">PV Gesamt: {fmtEur(pvOffer?.total_gross)}</div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* WP TAB */}
        <TabsContent value="wp" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">WP-Kennzahlen</CardTitle>
            </CardHeader>
            <CardContent className="grid md:grid-cols-4 gap-3">
              <div>
                <Label>Wärmebedarf (kWh)</Label>
                <Input type="number" value={wpOffer?.heat_demand_kwh || ""} onChange={(e) => setWpOffer({ ...wpOffer, heat_demand_kwh: e.target.value })} />
              </div>
              <div>
                <Label>Aktuelles Heizsystem</Label>
                <Input value={wpOffer?.current_heating_type || ""} onChange={(e) => setWpOffer({ ...wpOffer, current_heating_type: e.target.value })} />
              </div>
              <div>
                <Label>Aktuelle Heizkosten (€/Jahr)</Label>
                <Input type="number" value={wpOffer?.current_energy_cost_eur || ""} onChange={(e) => setWpOffer({ ...wpOffer, current_energy_cost_eur: e.target.value })} />
              </div>
              <div>
                <Label>Erw. Einsparung (€/Jahr)</Label>
                <Input type="number" value={wpOffer?.expected_savings_eur || ""} onChange={(e) => setWpOffer({ ...wpOffer, expected_savings_eur: e.target.value })} />
              </div>
            </CardContent>
          </Card>

          {WP_GROUPS.map((g) => (
            <Card key={g.key}>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center justify-between">
                  <span>{g.label} <Badge variant="outline" className="ml-2">{(wpByGroup[g.key] || []).length}</Badge></span>
                  <Button size="sm" variant="ghost" onClick={() => addWpItem(g.key)} data-testid={`add-wp-${g.key}`}>
                    <Plus className="h-3 w-3 mr-1" /> Position
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {(wpByGroup[g.key] || []).length === 0 ? (
                  <div className="text-xs text-muted-foreground italic px-4 py-3">Keine Positionen in dieser Gruppe.</div>
                ) : (
                  <Table>
                    <TableBody>
                      {(wpByGroup[g.key] || []).map((p) => (
                        <TableRow key={p.id}>
                          <TableCell className="w-1/2">
                            <Input value={p.title || ""} onChange={(e) => updateWpItem(p.id, "title", e.target.value)} className="h-8" />
                          </TableCell>
                          <TableCell className="text-right">
                            <Input type="number" step="0.01" value={p.quantity} onChange={(e) => updateWpItem(p.id, "quantity", e.target.value)} className="h-8 text-right w-20" />
                          </TableCell>
                          <TableCell>
                            <Input value={p.unit || "Stk"} onChange={(e) => updateWpItem(p.id, "unit", e.target.value)} className="h-8 w-20" />
                          </TableCell>
                          <TableCell className="text-right">
                            <Input type="number" step="0.01" value={p.unit_price ?? 0} onChange={(e) => updateWpItem(p.id, "unit_price", e.target.value)} className="h-8 text-right w-24" />
                          </TableCell>
                          <TableCell>
                            <Select value={p.product_group} onValueChange={(v) => updateWpItem(p.id, "product_group", v)}>
                              <SelectTrigger className="h-8 w-32"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {WP_GROUPS.map((gg) => <SelectItem key={gg.key} value={gg.key}>{gg.label}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Button size="icon" variant="ghost" onClick={() => removeWpItem(p.id)} data-testid={`remove-wp-${p.id}`}>
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          ))}

          <Card className="border-2 border-orange-300 bg-orange-50">
            <CardContent className="pt-6 text-right space-y-1">
              <div className="text-sm">Netto-Summe (live): <strong>{fmtEur(wpRecalc?.subtotal || wpOffer?.subtotal_net)}</strong></div>
              <div className="text-sm">MwSt. ({wpOffer?.vat_rate}%): <strong>{fmtEur(wpRecalc?.vat || wpOffer?.vat_amount)}</strong></div>
              <div className="text-2xl font-bold">WP Gesamt: {fmtEur(wpRecalc?.gross || wpOffer?.total_gross)}</div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="flex justify-end gap-3 sticky bottom-0 bg-background py-4 border-t">
        <Button variant="outline" onClick={() => navigate("/angebote-import")} data-testid="cancel-finalize">
          Abbrechen
        </Button>
        <Button onClick={handleFinalize} disabled={saving} data-testid="finalize-combined-btn">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
          Beide Angebote erstellen
        </Button>
      </div>
    </div>
  );
}
